import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Menu, X, Phone, MessageCircle, ArrowUpRight, Send, LogIn, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useSettings } from "@/context/SettingsContext";
import useHasBlog from "@/hooks/useHasBlog";
import Footer from "@/components/Footer";
import TopBar from "@/components/TopBar";
import CookieBanner from "@/components/CookieBanner";

const baseNav = [
    { to: "/servicos", label: "Serviços" },
    { to: "/trabalhos", label: "Obras & Transformações" },
    { to: "/como-trabalhamos", label: "Como Trabalhamos" },
    { to: "/sobre-nos", label: "Quem Somos" },
    { to: "/contactos", label: "Contactos" },
];

export default function PublicLayout() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { settings, whatsappLink } = useSettings();
    const loc = useLocation();
    const hasBlog = useHasBlog();

    // Insert "Blog" before "Contactos" only when at least one article is published.
    const nav = useMemo(() => {
        if (!hasBlog) return baseNav;
        const idx = baseNav.findIndex((n) => n.to === "/contactos");
        const withBlog = [...baseNav];
        withBlog.splice(idx, 0, { to: "/blog", label: "Blog" });
        return withBlog;
    }, [hasBlog]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);

    return (
        <div className="min-h-screen flex flex-col bg-[hsl(var(--cream))]">
            <TopBar />
            <header className="fixed top-12 left-0 right-0 z-40 px-4">
                <div className="max-w-6xl mx-auto flex justify-center">
                    <div className={`nav-pill-container transition-all ${scrolled ? "shadow-[0_12px_40px_-12px_rgba(24,53,47,0.35)]" : ""}`}>
                        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 pl-2 pr-3">
                            <span className="logo-mark text-2xl">H<span className="dot">.</span></span>
                            <span className="font-display font-semibold text-[13px] hidden sm:inline text-[hsl(var(--graphite))]">O Homem da Obra</span>
                        </Link>
                        <nav className="hidden lg:flex items-center gap-0.5">
                            {nav.map((n) => (
                                <Link key={n.to} to={n.to} data-testid={`nav-link-${n.to.slice(1)}`}
                                    className={`nav-item ${loc.pathname === n.to ? "active" : ""}`}>
                                    {n.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="flex items-center gap-1.5 ml-1 sm:ml-2">
                            <a href={`tel:${settings.phone.replace(/\s/g,'')}`} data-testid="header-call-btn"
                                className="w-9 h-9 rounded-full bg-[hsl(var(--cream))] flex items-center justify-center text-[hsl(var(--forest))] hover:bg-[hsl(var(--stone))] transition" aria-label="Ligar">
                                <Phone className="w-4 h-4" />
                            </a>
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger data-testid="header-login-btn"
                                    className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[hsl(var(--cream))] text-[hsl(var(--forest))] text-sm font-medium hover:bg-[hsl(var(--stone))] transition-colors outline-none">
                                    <LogIn className="w-4 h-4" /> Login <ChevronDown className="w-3.5 h-3.5" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="z-50 rounded-2xl p-1.5 min-w-[11rem]">
                                    <DropdownMenuItem asChild className="rounded-xl cursor-pointer py-2.5">
                                        <Link to="/admin/login" data-testid="header-login-gestao">Login Gestão</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild className="rounded-xl cursor-pointer py-2.5">
                                        <Link to="/parceiros/entrar" data-testid="header-login-parceiro">Login Parceiro</Link>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Link to="/orcamento" data-testid="header-orcamento-btn" className="btn-copper !py-2 !px-4 text-sm hidden sm:inline-flex">
                                Pedir orçamento
                                <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                            </Link>
                            <button onClick={() => setOpen(!open)} data-testid="header-menu-toggle"
                                className="lg:hidden w-9 h-9 rounded-full bg-[hsl(var(--forest))] text-white flex items-center justify-center" aria-label="Menu">
                                {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                </div>

                {open && (
                    <div className="lg:hidden mt-2 mx-auto max-w-md bg-white rounded-3xl shadow-xl px-4 py-5 space-y-1">
                        {nav.map((n) => (
                            <Link key={n.to} to={n.to} data-testid={`mobile-nav-link-${n.to.slice(1)}`}
                                className="block py-3 px-4 text-[hsl(var(--graphite))] text-base font-medium hover:bg-[hsl(var(--cream))] rounded-2xl">
                                {n.label}
                            </Link>
                        ))}
                        <div className="grid grid-cols-2 gap-2 pt-2">
                            <Link to="/admin/login" data-testid="mobile-login-gestao" className="py-3 px-4 text-center text-sm font-medium text-[hsl(var(--forest))] bg-[hsl(var(--cream))] rounded-2xl">Login Gestão</Link>
                            <Link to="/parceiros/entrar" data-testid="mobile-login-parceiro" className="py-3 px-4 text-center text-sm font-medium text-[hsl(var(--forest))] bg-[hsl(var(--cream))] rounded-2xl">Login Parceiro</Link>
                        </div>
                        <Link to="/orcamento" data-testid="mobile-orcamento-btn" className="block mt-3 btn-copper w-full justify-center">
                            Pedir orçamento
                            <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                )}
            </header>

            <main className="flex-1 pt-6 pb-20 md:pb-0"><Outlet /></main>
            <Footer />
            <CookieBanner />

            <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-lg border-t border-[hsl(var(--border))] grid grid-cols-3 shadow-2xl">
                <a href={`tel:${settings.phone.replace(/\s/g, "")}`} data-testid="sticky-mobile-ligar-btn" className="flex flex-col items-center gap-1 py-3">
                    <Phone className="w-4 h-4 text-[hsl(var(--forest))]" />
                    <span className="text-[11px] font-medium">Ligar</span>
                </a>
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="sticky-mobile-whatsapp-btn" className="flex flex-col items-center gap-1 py-3 border-x border-[hsl(var(--border))]">
                    <MessageCircle className="w-4 h-4 text-[hsl(var(--forest))]" />
                    <span className="text-[11px] font-medium">WhatsApp</span>
                </a>
                <Link to="/orcamento" data-testid="sticky-mobile-orcamento-btn" className="flex flex-col items-center gap-1 py-3 bg-[hsl(var(--copper))] text-white">
                    <Send className="w-4 h-4" />
                    <span className="text-[11px] font-medium">Orçamento</span>
                </Link>
            </div>
        </div>
    );
}
